import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import PropertyAPI from '../../api/property/property-api-factory';
import LoadingAnimation from '../../components/loading/loading';
import TenantApi from '../../api/tenant/tenant-api-factory';
import { CompanyAPI } from '../../api/company/company-api-factory';
import { FileApi } from '../../api/file/file-api';
import { PropertyFunctions } from '../../api/property/property-functions';
import { arrayAny } from '../../utils/arrayUtils';
import Comparer from '../../app/comparer';
import { ImageUploadModal } from '../../components/files/image-upload-modal';
import { ImageEditModal } from '../../components/files/image-edit-modal';
import { ImageGalleryElement } from '../../components/reusable-elements/image-gallery-element';

export const RoomDetails = () => {
    const { instance, inProgress, accounts } = useMsal();
    const { propertyId, roomId } = useParams();

    const api = useMemo(() => new PropertyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);
    const tenantApi = useMemo(() => new TenantApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const companyAPI = useMemo(() => new CompanyAPI(instance, inProgress, accounts, true), [instance, inProgress, accounts]);
    const fileApi = useMemo(() => new FileApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const [propertyDocument, setPropertyDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tenantInfo, setTenantInfo] = useState(null);
    const [showUploadImageModal, setShowUploadImageModal] = useState(false);
    const [showEditImageModal, setShowEditImageModal] = useState({ image: null, show: false });
    const [imageUploadProgress, setImageUploadProgress] = useState(0);
    const [imageUploadInProgress, setImageUploadInProgress] = useState(false);
    const [, setRenderToggle] = useState(false);

    useEffect(() => {
        if (!propertyId) return;
        setLoading(true);
        api.get(propertyId)
            .then(doc => setPropertyDocument(doc))
            .catch(e => setError(e))
            .finally(() => setLoading(false));
    }, [propertyId, api]);

    const matchRoomId = (r, id) => {
        const rid = r?.id ?? r?._id ?? r?.key ?? r?.roomId;
        return String(rid) === String(id);
    };

    const room = (propertyDocument?.property?.rooms || []).find(r => matchRoomId(r, roomId));
    const roomTenantId = room?.tenantId;

    const [roomDescription, setRoomDescription] = useState('');

    useEffect(() => {
        if (!room) return;
        const persistedDescription = typeof room.description === 'string' ? room.description : '';
        setRoomDescription(persistedDescription);
    }, [room]);

    useEffect(() => {
        let mounted = true;
        setTenantInfo(null);
        if (!roomTenantId) return;
        if (!tenantApi || typeof tenantApi.get !== 'function') return;
        tenantApi.get(roomTenantId)
            .then(t => { if (mounted) setTenantInfo(t); })
            .catch(() => { if (mounted) setTenantInfo(null); });
        return () => { mounted = false; };
    }, [roomTenantId, tenantApi]);

    const tenantList = propertyDocument?.property?.tenants ?? propertyDocument?.tenants ?? [];
    const tenant = tenantInfo ?? (tenantList || []).find(t => {
        const tid = t?.id ?? t?.tenantId ?? t?.key;
        return tid && String(tid) === String(room?.tenantId);
    });

    const roomMedia = room?.mediaGallery ?? [];

    const saveProperty = async (doc) => {
        const payload = doc ?? propertyDocument;
        const updated = await api.upsertProperty(payload);
        setPropertyDocument(updated);
        return updated;
    };

    const handleCancelShowEditImageModal = () => setShowEditImageModal({ image: null, show: false });

    const flipIsPrimaryFlagsForRoom = (gallery, current, newVersion, existingImageIndex) => {
        if (!gallery || !Array.isArray(gallery)) return;
        if (current.isPrimaryImage !== newVersion.isPrimaryImage && newVersion.isPrimaryImage) {
            for (let i = 0; i < gallery.length; i++) {
                if (i === existingImageIndex) continue;
                const tempVersion = structuredClone(gallery[i]);
                tempVersion.isPrimaryImage = false;
                gallery.splice(i, 1);
                gallery.splice(i, 0, tempVersion);
            }
        }
    };

    const handleDeleteRoomImageInfo = async (imageInfo) => {
        if (!propertyDocument || !room) return;
        const comparer = new Comparer();
        const docClone = structuredClone(propertyDocument);
        const rooms = docClone.property?.rooms || [];
        const targetRoom = rooms.find(r => matchRoomId(r, roomId));
        if (!targetRoom || !Array.isArray(targetRoom.mediaGallery)) return;
        const existingImageIndex = _.findIndex(targetRoom.mediaGallery, p => comparer.equals(p, imageInfo.image));
        if (existingImageIndex === -1) return;
        targetRoom.mediaGallery.splice(existingImageIndex, 1);
        await saveProperty(docClone);
        handleCancelShowEditImageModal();
        setRenderToggle(v => !v);
    };

    const handleUpdateRoomImageInfo = async (imageInfo) => {
        if (!propertyDocument || !room) return;
        const comparer = new Comparer();
        const docClone = structuredClone(propertyDocument);
        const rooms = docClone.property?.rooms || [];
        const targetRoom = rooms.find(r => matchRoomId(r, roomId));
        if (!targetRoom || !Array.isArray(targetRoom.mediaGallery)) return;
        const existingImageIndex = _.findIndex(targetRoom.mediaGallery, p => comparer.equals(p, imageInfo.image));
        if (existingImageIndex === -1) return;
        const current = targetRoom.mediaGallery[existingImageIndex];
        const newVersion = Object.assign({}, current, imageInfo.newDetails);

        newVersion.updated = DateTime.now().toUTC().toISO();
        if (accounts && accounts[0]) {
            newVersion.updatedBy = {
                userName: accounts[0].username,
                displayName: accounts[0].name,
                id: 'some_user_id'
            };
        }

        targetRoom.mediaGallery.splice(existingImageIndex, 1);
        targetRoom.mediaGallery.splice(existingImageIndex, 0, newVersion);

        flipIsPrimaryFlagsForRoom(targetRoom.mediaGallery, current, newVersion, existingImageIndex);

        await saveProperty(docClone);
        handleCancelShowEditImageModal();
        setRenderToggle(v => !v);
    };

    const setRoomImageStarRating = async (imageInfo, rating) => {
        if (!propertyDocument || !room) return;
        const comparer = new Comparer();
        const docClone = structuredClone(propertyDocument);
        const rooms = docClone.property?.rooms || [];
        const targetRoom = rooms.find(r => matchRoomId(r, roomId));
        if (!targetRoom || !Array.isArray(targetRoom.mediaGallery)) return;
        const existingImageIndex = _.findIndex(targetRoom.mediaGallery, p => comparer.equals(p, imageInfo));
        if (existingImageIndex === -1) return;
        const current = targetRoom.mediaGallery[existingImageIndex];
        const newVersion = structuredClone(current);
        newVersion.rating = rating;
        newVersion.updated = DateTime.now().toUTC().toISO();
        if (accounts && accounts[0]) {
            newVersion.updatedBy = {
                userName: accounts[0].username,
                displayName: accounts[0].name,
                id: 'some_user_id'
            };
        }
        targetRoom.mediaGallery.splice(existingImageIndex, 1);
        targetRoom.mediaGallery.splice(existingImageIndex, 0, newVersion);

        await saveProperty(docClone);
        setRenderToggle(v => !v);
    };

    const handleCancelAddImages = () => setShowUploadImageModal(false);

    const handleSaveDescription = async () => {
        if (!propertyDocument || !room) return;
        const docClone = structuredClone(propertyDocument);
        const rooms = docClone.property?.rooms || [];
        const targetRoom = rooms.find(r => matchRoomId(r, roomId));
        if (!targetRoom) return;
        targetRoom.description = roomDescription ?? '';

        await saveProperty(docClone);
    };

    const addNewImagesToRoomGallery = (collection, totalSize) => {
        if (!propertyDocument || !room) return;

        const savePropertyCallback = async (doc) => {
            try {
                const response = await api.upsertProperty(doc);
                const companyId = response?.property?.landlordOrOwner?.parent?.id;
                if (!companyId) {
                    throw new Error('Unable to determine company id for file upload.');
                }

                const company = await companyAPI.get(companyId);

                await fileApi.uploadFiles(
                    company.name,
                    `${response.id}/Rooms/${roomId}`,
                    collection,
                    (data) => {
                        const totalRemaining = _.sumBy(data, d => d.remainingBytes);
                        const progress = 1 - (totalRemaining / totalSize);
                        setImageUploadProgress(progress);
                    }
                );

                setImageUploadInProgress(false);
                setImageUploadProgress(0);
                setPropertyDocument(response);
            } catch (e) {
                console.log('Error occurred uploading room images.', e);
                setImageUploadInProgress(false);
            } finally {
                setShowUploadImageModal(false);
            }
        };

        const docClone = structuredClone(propertyDocument);
        const rooms = docClone.property?.rooms || [];
        const targetRoom = rooms.find(r => matchRoomId(r, roomId));
        if (!targetRoom) return;

        const newImageItems = collection.map(i => ({
            isPrimaryImage: false,
            path: 'placeholder',
            name: i.name.split(':')[0],
            caption: i.name.split(':')[0],
            description: 'Please enter meaningful description',
            rating: 0,
            title: 'Please enter title',
            dimensions: { width: 0, height: 0 }
        }));

        targetRoom.mediaGallery = [].concat(targetRoom.mediaGallery || [], newImageItems);

        const dimensionsCallback = (img, dims) => {
            const roomForUpdate = (docClone.property?.rooms || []).find(r => matchRoomId(r, roomId));
            if (!roomForUpdate || !Array.isArray(roomForUpdate.mediaGallery)) return;
            const image = _.find(roomForUpdate.mediaGallery, p => p.path === 'placeholder' && p.name && p.name.endsWith(img.name.split(':')[0]));
            if (!image) return;
            image.path = img.src;
            image.dimensions = dims;

            const gallery = [];
            roomForUpdate.mediaGallery.map(pg => gallery.push(pg));
            roomForUpdate.mediaGallery = gallery;

            if (!_.find(gallery, p => p.path === 'placeholder') &&
                arrayAny(gallery.filter(p => (p.path || '').startsWith('data:image')))) {
                savePropertyCallback(docClone);
            }
        };

        PropertyFunctions.readImageFiles(collection, dimensionsCallback);
    };

    const handleAddNewImages = (collection) => {
        const totalSize = _.sumBy(collection, f => f.size);
        if (!imageUploadProgress) {
            setImageUploadInProgress(true);
            setImageUploadProgress(0);
            addNewImagesToRoomGallery(collection, totalSize);
        }
    };

    // Guard returns after hooks
    if (loading) return <LoadingAnimation />;
    if (error) return <div className="alert alert-danger">Error loading property: {String(error)}</div>;
    if (!propertyDocument) return <div className="text-muted">Property not found</div>;

    if (!room) {
        return (
            <div>
                <ol className="breadcrumb float-xl-end">
                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                    <li className="breadcrumb-item"><Link to="/property/search">Property Search</Link></li>
                    <li className="breadcrumb-item"><Link to={`/property/detail/${propertyId}`}>Details</Link></li>
                    <li className="breadcrumb-item active">Room</li>
                </ol>
                <h1 className="page-header">Room not found</h1>
                <p className="text-muted">The requested room could not be found on this property.</p>
            </div>
        );
    }

    return (
        <div>
            <ol className="breadcrumb float-xl-end">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/property/search">Property Search</Link></li>
                <li className="breadcrumb-item"><Link to={`/property/detail/${propertyId}`}>Details</Link></li>
                <li className="breadcrumb-item active">Room</li>
            </ol>

            <h1 className="page-header">Room Details | <small>{propertyDocument?.property?.address?.doorNumberOrName} {propertyDocument?.property?.address?.roadName}</small></h1>

            <div className="card mb-3">
                <div className="card-body">
                    <h5>Room</h5>
                    <p><strong>Type:</strong> {room.roomType}</p>
                    {room.roomType === 'Bedroom' && <p><strong>Room number:</strong> {room.roomNumber ?? 'N/A'}</p>}
                    <p><strong>Tenant:</strong> {tenant ? (tenant.displayName || tenant.name || tenant.fullName) : 'Unassigned'}</p>
                    <div className="mt-3">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control w-50"
                            rows="5"
                            value={roomDescription}
                            onChange={e => setRoomDescription(e.target.value)}
                            placeholder="Describe the key qualities and value of this room..."
                            style={{ minHeight: '8rem' }}
                        />
                        <div className="mt-2 text-end">
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleSaveDescription}
                                disabled={!propertyDocument}
                            >
                                Save description
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">Videos &amp; Photos</h5>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => setShowUploadImageModal(true)}
                        >
                            <i className="fa fa-plus me-1"></i> Add Media
                        </button>
                    </div>

                    <ImageGalleryElement
                        photos={roomMedia}
                        setShowUploadImageModal={setShowUploadImageModal}
                        setShowEditImageModal={setShowEditImageModal}
                        setStarRating={setRoomImageStarRating}
                    />
                </div>
            </div>

            <ImageUploadModal
                show={showUploadImageModal}
                handleCancel={handleCancelAddImages}
                handleSave={handleAddNewImages}
                imageUploadInProgress={imageUploadInProgress}
                imageUploadProgress={imageUploadProgress}
            />

            <ImageEditModal
                show={showEditImageModal.show}
                imageInfo={showEditImageModal.image}
                handleCancel={handleCancelShowEditImageModal}
                handleSave={handleUpdateRoomImageInfo}
                handleDeleteImage={handleDeleteRoomImageInfo}
            />

        </div>
    );
};

export default RoomDetails;
